import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { getPoliciesByRange, setGenerationStep } from 'api/operations/policyService';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { EnrollmentType } from 'types/enrollmentService';
import { GenerationStep, Policy } from 'types/policyService';

export const GenerationStepTitle: Record<GenerationStep, string> = Object.freeze({
  [GenerationStep.INACTIVE_POLICY]: 'Inactive Policy',
  [GenerationStep.OUTPUT_GENERATION_FAILED]: 'Output Generation Failed',
  [GenerationStep.NOT_STARTED]: 'Not Started',
  [GenerationStep.GROUP_FILES_GENERATED]: 'Group Files Generated',
  [GenerationStep.MAINTENANCE_FILES_GENERATED]: 'Maintenance Files Generated',
  [GenerationStep.EDI_GENERATION_READY]: 'EDI Generation Ready',
  [GenerationStep.EDI_GENERATED]: 'EDI Generated',
  [GenerationStep.MEMBER_CARD_GENERATION_READY]: 'Member Card Generation Ready',
  [GenerationStep.COMPLETED]: 'Completed',
});

export const EnrollmentPeriodTypeTitle: Record<EnrollmentType, string> = Object.freeze({
  [EnrollmentType.NEW_SALE]: 'New Sale',
  [EnrollmentType.RESUBMIT_NEW_SALE]: 'Resubmit New Sale',
  [EnrollmentType.RENEWAL]: 'Renewal',
  [EnrollmentType.INSTALLED]: 'Installed',
  [EnrollmentType.TERMINATED]: 'Terminated',
});

export type PolicyModalType = 'UPDATE_STEP' | 'SHOW_INFO';

export default class PolicyDashboardStore {
  rootStore: RootStore;
  loading = false;
  policies: Policy[] = [];
  totalElements = 0;
  totalPages = 0;
  page = 1;
  sponsorId = '';
  step: GenerationStep | string = '';
  date = {
    from: new Date().toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  };

  selectedPolicy: Policy | null = null;
  // Modal properties
  isModalOpen = false;
  modalType: PolicyModalType | null = null;
  modalLoading = false;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setModalLoading(loadingStatus: boolean): void {
    this.modalLoading = loadingStatus;
  }

  public closeModal(): void {
    this.modalType = null;
    this.isModalOpen = false;
    this.selectedPolicy = null;
  }

  public openModal(policy: Policy, modalType: PolicyModalType): void {
    this.modalType = modalType;
    this.isModalOpen = true;
    this.selectedPolicy = policy;
  }

  public setSponsorId(sponsorId: string): void {
    this.sponsorId = sponsorId;
  }

  public setStep(step: GenerationStep): void {
    this.step = step;
  }

  public setPage(page: number): void {
    this.page = page;
  }

  public updateDate(from: string, to: string): void {
    this.date = { from, to };
  }

  // eslint-disable-next-line class-methods-use-this
  public getStepOptions(addOptionAll = false): OptionType[] {
    const stepOptions = Object.keys(GenerationStepTitle).map((step) => ({
      value: step,
      label: GenerationStepTitle[step as GenerationStep],
    }));
    return addOptionAll ? [{ value: '', label: 'All' }, ...stepOptions] : stepOptions;
  }

  public async getPolicies(showLoader = true): Promise<void> {
    if (showLoader) this.setLoading(true);
    const result = await getPoliciesByRange(this.date.from, this.date.to, this.sponsorId, this.step, this.page);
    runInAction(() => {
      this.totalElements = result.totalElements;
      this.totalPages = result.totalPages;
      this.policies = result.result;
      if (showLoader) this.setLoading(false);
    });
  }

  public async updatePolicyStep(policyId: string, step: GenerationStep): Promise<void> {
    this.setModalLoading(true);
    const res = await setGenerationStep(policyId, step);
    await runInAction(async () => {
      const result = res.data?.setGenerationStep;
      if (result?.id) {
        this.rootStore.filesTrackingController.showToast(
          'The policy step has been successfully updated.',
          ToastColorTypes.SUCCESS,
        );
        await this.getPolicies(false);
      } else {
        this.rootStore.filesTrackingController.showToast(
          'An error occurred during the attempt to update the policy step. Please try again later.',
          ToastColorTypes.WARNING,
        );
      }
      this.setModalLoading(false);
    });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
